/**
 * Imports screen  –  ticket #778
 *
 * Allows the user to:
 * - Upload a transport zip file for import (with optional name-override rules)
 * - Toggle test mode (dry-run) to preview changes before committing
 * - See a preview of new / overwrite / error objects with diff details
 * - Confirm or cancel the actual import after preview
 * - View all past imports and their status
 */
import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { AppDispatch, RootState, useAppDispatch } from 'redux/store';
import FormatDateTime from 'components/common/FormatDateTime';
import {
  fetchTransportImports,
  importTransportZip,
  confirmImport,
  clearImportPreview,
  rescheduleInstances,
  clearScheduledInstances,
  type ImportPreviewItem,
} from 'redux/slices/transportsSlice';
import { ScheduledInstanceRowStatusBadges } from './ScheduledInstanceRowStatusBadges';

function formatDiffValue(val: unknown): string {
  if (typeof val === 'object' && val !== null) return JSON.stringify(val);
  return String(val ?? '');
}

// ---------------------------------------------------------------------------
const statusVariant: Record<string, string> = {
  pending: 'warning',
  success: 'success',
  failed: 'danger',
};

const previewStatusVariant: Record<string, string> = {
  new: 'success',
  overwrite: 'warning',
  error: 'danger',
};

// ---------------------------------------------------------------------------
const ListImports: React.FC = () => {
  const dispatch = useAppDispatch();
  const { imports, importPreview, scheduledInstances, loading } = useSelector((s: RootState) => s.transports);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [testMode, setTestMode] = useState(false);
  const [selectedInstances, setSelectedInstances] = useState<Set<number>>(new Set());
  const [historyPreviewImportId, setHistoryPreviewImportId] = useState<number | null>(null);

  // name override rules:  [{from, to}]
  const [overrides, setOverrides] = useState<{ from: string; to: string }[]>([]);
  const [overrideFrom, setOverrideFrom] = useState('');
  const [overrideTo, setOverrideTo] = useState('');

  useEffect(() => {
    dispatch(fetchTransportImports());
  }, [dispatch]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Imports | Norscom';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] ?? null);
    dispatch(clearImportPreview());
  };

  const handleAddOverride = () => {
    if (!overrideFrom.trim()) return;
    setOverrides((prev) => [...prev, { from: overrideFrom.trim(), to: overrideTo.trim() }]);
    setOverrideFrom('');
    setOverrideTo('');
  };

  const handleRemoveOverride = (idx: number) => {
    setOverrides((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    const nameOverrides = overrides.reduce<Record<string, string>>((acc, r) => {
      acc[r.from] = r.to;
      return acc;
    }, {});
    await dispatch(importTransportZip({ file: selectedFile, nameOverrides, testMode }));
  };

  // Default-select all scheduled instances when they appear
  useEffect(() => {
    if (scheduledInstances.length > 0) {
      setSelectedInstances(new Set(scheduledInstances.map((si) => si.node_instance_id)));
    }
  }, [scheduledInstances]);

  const handleConfirm = async () => {
    if (!importPreview?.confirm_token) return;
    const result = await dispatch(confirmImport({ confirmToken: importPreview.confirm_token }));
    if (confirmImport.fulfilled.match(result)) {
      setSelectedFile(null);
      setOverrides([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      dispatch(fetchTransportImports());
    }
    // On failure: toast is shown by the slice, UI state preserved for retry.
  };

  const handleCancel = () => {
    dispatch(clearImportPreview());
  };

  const handleReset = () => {
    setSelectedFile(null);
    setOverrides([]);
    dispatch(clearImportPreview());
    dispatch(clearScheduledInstances());
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleInstance = (id: number) => {
    setSelectedInstances((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleReschedule = async () => {
    if (selectedInstances.size === 0) return;
    await dispatch(rescheduleInstances({ nodeInstanceIds: Array.from(selectedInstances) }));
    dispatch(fetchTransportImports());
  };

  const handleSkipReschedule = () => {
    dispatch(clearScheduledInstances());
  };

  // Preview counts (single pass)
  const previewCounts = (importPreview?.preview ?? []).reduce(
    (acc, p) => {
      if (p.status in acc) acc[p.status]++;
      return acc;
    },
    { new: 0, overwrite: 0, error: 0 } as Record<string, number>
  );

  const selectedHistoryImport = imports.find((imp) => imp.id === historyPreviewImportId);
  const historyPreview = selectedHistoryImport?.preview_data;
  const historyPreviewCounts = (historyPreview?.preview ?? []).reduce(
    (acc, p) => {
      if (p.status in acc) acc[p.status]++;
      return acc;
    },
    { new: 0, overwrite: 0, error: 0 } as Record<string, number>
  );

  return (
    <div className="p-3">
      <h4 className="mb-4">Imports</h4>

      <Row className="mb-4">
        {/* -- Upload card -------------------------------------------------- */}
        <Col md={6}>
          <Card>
            <Card.Header>
              <strong>Upload Transport ZIP</strong>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>File (.zip)</Form.Label>
                <Form.Control type="file" accept=".zip" ref={fileInputRef} onChange={handleFileChange} />
              </Form.Group>

              {/* Test mode toggle */}
              <Form.Check
                type="switch"
                id="test-mode-switch"
                label="Test mode (validate only, do not import)"
                checked={testMode}
                onChange={(e) => setTestMode(e.target.checked)}
                className="mb-3"
              />

              {/* Name override rules */}
              <Form.Label>Name Override Rules</Form.Label>
              <p className="text-muted small mb-2">
                Rename references during import using partial matching (e.g. replace all occurrences of &quot;SAPACC&quot; with &quot;SAPPRD&quot;).
              </p>
              <div className="d-flex gap-2 mb-2">
                <Form.Control type="text" placeholder="Search text" value={overrideFrom} onChange={(e) => setOverrideFrom(e.target.value)} size="sm" />
                <Form.Control type="text" placeholder="Replace with" value={overrideTo} onChange={(e) => setOverrideTo(e.target.value)} size="sm" />
                <Button variant="outline-secondary" size="sm" onClick={handleAddOverride}>
                  Add
                </Button>
              </div>
              {overrides.length > 0 && (
                <ul className="list-unstyled mb-3">
                  {overrides.map((r, i) => (
                    <li key={i} className="d-flex align-items-center gap-2 small">
                      <span>
                        <code>{r.from}</code> &rarr; <code>{r.to}</code>
                      </span>
                      <Button variant="link" size="sm" className="text-danger p-0" onClick={() => handleRemoveOverride(i)}>
                        &times;
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="d-flex gap-2">
                <Button variant="primary" onClick={handleImport} disabled={!selectedFile || loading.importZip}>
                  {loading.importZip ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Validating...
                    </>
                  ) : testMode ? (
                    'Validate'
                  ) : (
                    'Upload & Preview'
                  )}
                </Button>
                {importPreview && (
                  <Button variant="outline-secondary" onClick={handleReset}>
                    Reset
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* -- Preview card ------------------------------------------------- */}
        {importPreview && (
          <Col md={6}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <strong>Import Preview</strong>
                <div>
                  <Badge bg="success" className="me-1">
                    {previewCounts.new} new
                  </Badge>
                  <Badge bg="warning" className="me-1">
                    {previewCounts.overwrite} overwrite
                  </Badge>
                  <Badge bg="danger">{previewCounts.error} error</Badge>
                </div>
              </Card.Header>
              <Card.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {importPreview.errors.length > 0 && (
                  <div className="alert alert-danger py-2 small mb-3">
                    <strong>Validation errors:</strong>
                    <ul className="mb-0 ps-3">
                      {importPreview.errors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <Table hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.preview.map((item: ImportPreviewItem, i: number) => (
                      <tr key={i}>
                        <td>
                          <Badge bg="secondary" className="text-capitalize">
                            {item.object_type.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="small">{item.name}</td>
                        <td>
                          <Badge bg={previewStatusVariant[item.status] ?? 'secondary'}>{item.status}</Badge>
                        </td>
                        <td className="small">
                          {item.status === 'error' && <span className="text-danger">{item.reason}</span>}
                          {item.status === 'overwrite' && item.changes && item.changes.length > 0 && (
                            <details>
                              <summary className="text-warning" style={{ cursor: 'pointer' }}>
                                {item.changes.length} field(s) changed
                              </summary>
                              <ul className="list-unstyled mt-1 mb-0">
                                {item.changes.map((c, ci) => (
                                  <li key={ci} className="mb-1">
                                    <strong>{c.field}:</strong> <span className="text-danger text-decoration-line-through">{formatDiffValue(c.old)}</span>{' '}
                                    &rarr; <span className="text-success">{formatDiffValue(c.new)}</span>
                                  </li>
                                ))}
                              </ul>
                            </details>
                          )}
                          {item.status === 'overwrite' && (!item.changes || item.changes.length === 0) && <span className="text-muted">No field changes</span>}
                          {item.status === 'new' && <span className="text-muted">Will be created</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
              {/* Action buttons */}
              {!importPreview.test_mode && importPreview.confirm_token && (
                <Card.Footer className="d-flex gap-2">
                  <Button variant="success" onClick={handleConfirm} disabled={loading.confirmImport || !importPreview.valid}>
                    {loading.confirmImport ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Importing...
                      </>
                    ) : (
                      'Confirm Import'
                    )}
                  </Button>
                  <Button variant="outline-secondary" onClick={handleCancel}>
                    Cancel
                  </Button>
                  {!importPreview.valid && <span className="text-danger small align-self-center ms-2">Fix errors before importing</span>}
                </Card.Footer>
              )}
              {importPreview.test_mode && (
                <Card.Footer>
                  <span className={importPreview.valid ? 'text-success' : 'text-danger'}>
                    {importPreview.valid ? 'Validation passed - all objects can be imported.' : 'Validation failed - see errors above.'}
                  </span>
                </Card.Footer>
              )}
            </Card>
          </Col>
        )}
      </Row>

      {/* -- Reschedule panel ------------------------------------------------ */}
      {scheduledInstances.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card border="info">
              <Card.Header className="bg-info bg-opacity-10">
                <strong>Scheduled Instances to Update</strong>
                <span className="text-muted ms-2 small">
                  {scheduledInstances.length} instance(s) with updated definitions are currently in a scheduled/pending state
                </span>
              </Card.Header>
              <Card.Body>
                <Table hover size="sm" className="mb-3">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <Form.Check
                          type="checkbox"
                          checked={selectedInstances.size === scheduledInstances.length}
                          onChange={() => {
                            if (selectedInstances.size === scheduledInstances.length) {
                              setSelectedInstances(new Set());
                            } else {
                              setSelectedInstances(new Set(scheduledInstances.map((si) => si.node_instance_id)));
                            }
                          }}
                        />
                      </th>
                      <th>Instance ID</th>
                      <th>Name</th>
                      <th>Definition</th>
                      <th>Zone</th>
                      <th>Status</th>
                      <th>Scheduled Start</th>
                      <th>In Flow</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduledInstances.map((si) => (
                      <tr key={si.node_instance_id}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedInstances.has(si.node_instance_id)}
                            onChange={() => toggleInstance(si.node_instance_id)}
                          />
                        </td>
                        <td>{si.node_instance_id}</td>
                        <td>{si.instance_name || '--'}</td>
                        <td>{si.definition_name}</td>
                        <td>{si.zone_name || '--'}</td>
                        <td>
                          {/* `node_instances.status` is a nullable column; the badge renders a display string, and `''` is what the old
                            `str()` declaration substituted, so an unset status shows as it did before. */}
                          <ScheduledInstanceRowStatusBadges status={si.status ?? ''} missingMandatoryParameters={si.missing_mandatory_parameters} />
                        </td>
                        <td className="small">{si.scheduled_start_datetime ? new Date(si.scheduled_start_datetime).toLocaleString() : '--'}</td>
                        <td>{si.flow_instance_id ? `Flow #${si.flow_instance_id}` : '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="d-flex gap-2">
                  <Button variant="primary" onClick={handleReschedule} disabled={selectedInstances.size === 0 || loading.reschedule}>
                    {loading.reschedule ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : (
                      `Update ${selectedInstances.size} instance(s)`
                    )}
                  </Button>
                  <Button variant="outline-secondary" onClick={handleSkipReschedule}>
                    Skip
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* -- Import history ------------------------------------------------- */}
      <h5 className="mb-3">Import History</h5>
      {loading.fetchImports ? (
        <div className="text-center py-4">
          <Spinner />
        </div>
      ) : (
        <Table hover responsive size="sm">
          <thead>
            <tr>
              <th>#</th>
              <th>Filename</th>
              <th>Status</th>
              <th>Notes</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {imports.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted py-4">
                  No imports yet.
                </td>
              </tr>
            )}
            {imports.map((imp) => (
              <tr key={imp.id}>
                <td>{imp.id}</td>
                <td>{imp.filename}</td>
                <td>
                  <Badge bg={statusVariant[imp.status] ?? 'secondary'}>{imp.status}</Badge>
                </td>
                <td className="text-muted small">
                  {imp.notes ?? '--'}
                  {imp.preview_data?.preview?.length ? (
                    <div className="mt-1">
                      <Button size="sm" variant="link" className="p-0" onClick={() => setHistoryPreviewImportId(imp.id)}>
                        View preview details
                      </Button>
                    </div>
                  ) : null}
                </td>
                <td className="small">{imp.created_at ? <FormatDateTime value={imp.created_at} /> : '--'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={historyPreviewImportId !== null} onHide={() => setHistoryPreviewImportId(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Import Preview History {selectedHistoryImport ? `#${selectedHistoryImport.id}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!historyPreview ? (
            <div className="text-muted">No preview details stored for this import.</div>
          ) : (
            <>
              <div className="mb-3">
                <Badge bg="success" className="me-1">
                  {historyPreviewCounts.new} new
                </Badge>
                <Badge bg="warning" className="me-1">
                  {historyPreviewCounts.overwrite} overwrite
                </Badge>
                <Badge bg="danger">{historyPreviewCounts.error} error</Badge>
              </div>

              {historyPreview.errors?.length > 0 && (
                <div className="alert alert-danger py-2 small mb-3">
                  <strong>Validation / import errors:</strong>
                  <ul className="mb-0 ps-3">
                    {historyPreview.errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Table hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {historyPreview.preview?.map((item: ImportPreviewItem, i: number) => (
                    <tr key={i}>
                      <td>
                        <Badge bg="secondary" className="text-capitalize">
                          {item.object_type.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="small">{item.name}</td>
                      <td>
                        <Badge bg={previewStatusVariant[item.status] ?? 'secondary'}>{item.status}</Badge>
                      </td>
                      <td className="small">
                        {item.status === 'error' && <span className="text-danger">{item.reason}</span>}
                        {item.status === 'overwrite' && item.changes && item.changes.length > 0 && (
                          <details>
                            <summary className="text-warning" style={{ cursor: 'pointer' }}>
                              {item.changes.length} field(s) changed
                            </summary>
                            <ul className="list-unstyled mt-1 mb-0">
                              {item.changes.map((c, ci) => (
                                <li key={ci} className="mb-1">
                                  <strong>{c.field}:</strong> <span className="text-danger text-decoration-line-through">{formatDiffValue(c.old)}</span> &rarr;{' '}
                                  <span className="text-success">{formatDiffValue(c.new)}</span>
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                        {item.status === 'overwrite' && (!item.changes || item.changes.length === 0) && <span className="text-muted">No field changes</span>}
                        {item.status === 'new' && <span className="text-muted">Created</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setHistoryPreviewImportId(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ListImports;
